import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useResourceWorkshopBySlug, ResourceWorkshopItem } from "@/hooks/useResourceWorkshops";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Loader2, ExternalLink, FileText, Link as LinkIcon, Download, Lock, AlignLeft, Code2, Library, Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import DOMPurify from 'dompurify';

export default function ResourceWorkshopPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { data, isLoading, error } = useResourceWorkshopBySlug(slug);
  const [embedItem, setEmbedItem] = useState<ResourceWorkshopItem | null>(null);

  const workshop = data?.workshop;
  const items = (data?.items || []).filter((i) => !i.is_hidden);

  useEffect(() => {
    if (workshop) {
      document.title = `${workshop.name} | Taller`;
      const meta = document.querySelector('meta[name="description"]');
      const desc = (workshop.description || workshop.name).slice(0, 155);
      if (meta) meta.setAttribute("content", desc);
    }
  }, [workshop]);

  if (isLoading || authLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !workshop || !workshop.is_active) {
    return (
      <div className="max-w-md mx-auto py-12">
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <h1 className="text-2xl font-display font-bold">Taller no encontrado</h1>
            <p className="text-muted-foreground">El taller que buscas no existe o ya no está disponible.</p>
            <Button onClick={() => navigate("/")}>Volver al inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (workshop.visibility === "authenticated" && !user) {
    return (
      <div className="max-w-md mx-auto py-12">
        <Card>
          <CardContent className="py-12 text-center space-y-4">
            <Lock className="w-10 h-10 mx-auto text-primary" />
            <h1 className="text-2xl font-display font-bold">{workshop.name}</h1>
            <p className="text-muted-foreground">
              Este taller es solo para usuarios registrados. Inicia sesión para acceder a los recursos.
            </p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => navigate(`/auth?next=/taller/${workshop.slug}`)}>
                Iniciar sesión
              </Button>
              <Button variant="outline" onClick={() => navigate(`/auth?mode=signup&next=/taller/${workshop.slug}`)}>
                Crear cuenta
              </Button>
              <Button variant="ghost" onClick={() => navigate("/")}>Inicio</Button>
            </div>

          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {workshop.cover_image_url && (
        <div className="w-full h-48 md:h-64 bg-muted overflow-hidden rounded-xl">
          <img src={workshop.cover_image_url} alt={workshop.name} className="w-full h-full object-cover" />
        </div>
      )}

      <header className="space-y-3">
        <h1 className="text-3xl md:text-4xl font-display font-bold">{workshop.name}</h1>
        {workshop.description && (
          <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
            {workshop.description}
          </p>
        )}
      </header>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">Recursos</h2>
        {items.length === 0 ? (
          <p className="text-muted-foreground text-sm">Aún no hay recursos disponibles.</p>
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              if (it.type === "text") {
                return (
                  <div key={it.id} className="flex gap-3 p-4 border rounded-lg bg-card">
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <AlignLeft className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <p className="font-medium">{it.title}</p>
                      {it.description && (
                        <p className="text-sm text-muted-foreground">{it.description}</p>
                      )}
                      {it.content && (
                        <div
                          className="prose prose-sm dark:prose-invert max-w-none mt-2"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(it.content) }}
                        />
                      )}
                    </div>
                  </div>
                );
              }
              if (it.type === "embed" || it.type === "content") {
                const isBank = it.type === "content";
                const meta = isBank
                  ? [it.content_type, it.content_duration].filter(Boolean).join(" · ")
                  : null;
                return (
                  <button
                    key={it.id}
                    onClick={() => setEmbedItem(it)}
                    className="w-full text-left flex items-center gap-3 p-4 border rounded-lg bg-card hover:bg-accent/40 transition group"
                  >
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {isBank && it.content_thumbnail_url ? (
                        <img src={it.content_thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : isBank ? (
                        <Library className="w-5 h-5 text-primary" />
                      ) : (
                        <Code2 className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium group-hover:text-primary truncate">{it.title}</p>
                      {it.description && (
                        <p className="text-sm text-muted-foreground truncate">{it.description}</p>
                      )}
                      {meta && <p className="text-xs text-muted-foreground truncate">{meta}</p>}
                    </div>
                    <ExternalLink className="w-5 h-5 text-muted-foreground" />
                  </button>
                );
              }

              return (
                <a
                  key={it.id}
                  href={it.url || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 p-4 border rounded-lg bg-card hover:bg-accent/40 transition group"
                >
                  <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                    {it.type === "link"
                      ? <LinkIcon className="w-5 h-5 text-primary" />
                      : <FileText className="w-5 h-5 text-primary" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium group-hover:text-primary truncate">{it.title}</p>
                    {it.description && (
                      <p className="text-sm text-muted-foreground truncate">{it.description}</p>
                    )}
                  </div>
                  {it.type === "file"
                    ? <Download className="w-5 h-5 text-muted-foreground" />
                    : <ExternalLink className="w-5 h-5 text-muted-foreground" />}
                </a>
              );
            })}
          </div>
        )}
      </section>

      <EmbedModal item={embedItem} onClose={() => setEmbedItem(null)} />
    </div>
  );
}

function EmbedModal({ item, onClose }: { item: ResourceWorkshopItem | null; onClose: () => void }) {
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!item) setFullscreen(false);
  }, [item]);

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className={cn(
          "p-0 gap-0 flex flex-col [&>button]:hidden",
          fullscreen
            ? "max-w-[100vw] w-screen h-screen max-h-screen rounded-none"
            : "max-w-5xl w-[95vw] h-[85vh]"
        )}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <p className="font-medium truncate pr-3">{item?.title}</p>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => setFullscreen((f) => !f)}
              className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
              title={fullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
            >
              {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
              title="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 bg-muted">
          {(() => {
            if (!item) return null;
            const isBank = item.type === "content";
            const srcUrl = isBank ? item.content_vimeo_player_url || item.content_url : item.url;
            const srcDoc = isBank ? item.content_html : item.content;
            if (srcUrl) {
              return (
                <iframe
                  src={srcUrl}
                  title={item.title}
                  className="w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              );
            }
            if (srcDoc) {
              return (
                <iframe
                  srcDoc={srcDoc}
                  title={item.title}
                  className="w-full h-full border-0 bg-white"
                  sandbox="allow-scripts allow-popups allow-forms allow-presentation"
                  allowFullScreen
                />
              );
            }
            return (
              <div className="h-full flex items-center justify-center p-6 text-sm text-muted-foreground">
                Este contenido no tiene una fuente visualizable.
              </div>
            );
          })()}
        </div>

      </DialogContent>
    </Dialog>
  );
}
